<template>
  <div class="kb-rows">
    <!-- Membership dues -->
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          Membership dues
        </h4>
        <p class="kb-row-hint">
          {{ duesStatus.displayText }}
        </p>
      </div>
      <div class="kb-row-aside">
        <span
          class="kb-tag"
          :class="duesTagClass(duesStatus.statusType)"
        >{{ duesStatus.tagValue }}</span>
        <template v-if="duesStatus.shouldPay || duesStatus.statusType === DuesStatusType.PAID">
          <KbSpinner
            v-if="isUpdating || isRedeeming"
            label="Saving"
          />
          <!-- Not paid: show Mark as Paid and Redeem Code -->
          <template v-if="duesStatus.statusType !== DuesStatusType.PAID">
            <Button
              label="Redeem code"
              icon="pi pi-ticket"
              text
              severity="secondary"
              :disabled="isUpdating || isRedeeming"
              size="small"
              @click="showRedeemDialog = true"
            />
            <Button
              label="Mark as paid"
              icon="pi pi-check"
              :disabled="isUpdating"
              size="small"
              @click="toggleMembershipPayment"
            />
          </template>
          <!-- Paid via pool ticket: show Return to Pool -->
          <Button
            v-else-if="pooledMembershipInfo"
            label="Return to pool"
            icon="pi pi-replay"
            text
            severity="secondary"
            :disabled="isUpdating"
            size="small"
            @click="showUnredeemConfirm = true"
          />
          <!-- Paid through Stripe or Eventbrite: only a refund there undoes it -->
          <span
            v-else-if="paidMembership"
            class="kb-row-meta"
          >Refund in Stripe to undo</span>
          <!-- Paid via admin comp: show Mark as Unpaid -->
          <Button
            v-else
            label="Mark as unpaid"
            icon="pi pi-times"
            text
            severity="danger"
            :disabled="isUpdating"
            size="small"
            @click="toggleMembershipPayment"
          />
        </template>
      </div>

      <div class="kb-row-body">
        <p
          v-if="duesStatus.paidAt"
          class="kb-row-meta m-0"
        >
          Paid on {{ formatDate(duesStatus.paidAt) }}
        </p>

        <!-- Pooled membership info -->
        <p
          v-if="pooledMembershipInfo"
          class="kb-row-note"
        >
          <span v-if="pooledMembershipInfo.redeemCode">Redeemed code <span class="font-mono">{{ pooledMembershipInfo.redeemCode }}</span></span>
          <span v-else>Paid with a pool ticket</span>
          from {{ pooledMembershipInfo.subkreweName || pooledMembershipInfo.subkreweId }}<span v-if="pooledMembershipInfo.redeemedByAdminName">, redeemed by {{ pooledMembershipInfo.redeemedByAdminName }}</span>
        </p>
        <!-- Admin-comped membership info -->
        <p
          v-else-if="adminCompedMembershipInfo"
          class="kb-row-note"
        >
          Marked as paid by {{ adminCompedMembershipInfo.adminName }}
        </p>

        <!-- Why this member doesn't pay -->
        <p
          v-if="duesStatus.statusType === DuesStatusType.EXEMPT_REDSHIRT"
          class="kb-row-note"
        >
          Red shirts don't pay annual dues.
          <template v-if="!hasVipTicket">
            A VIP upgrade is still a separate purchase.
          </template>
        </p>
        <p
          v-else-if="duesStatus.statusType === DuesStatusType.EXEMPT_PENDING_APPROVAL"
          class="kb-row-note"
        >
          Temporarily exempt while their SubKrewe's application is pending. Once it's approved, they need to pay annual dues.
          <template v-if="!hasVipTicket">
            A VIP upgrade is still a separate purchase.
          </template>
        </p>
      </div>

      <!-- Unredeem confirmation dialog -->
      <Dialog
        v-model:visible="showUnredeemConfirm"
        header="Return ticket to pool"
        modal
        :style="{ width: '28rem' }"
        :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      >
        <div class="kb-form">
          <p class="kb-dialog-lead">
            <strong>{{ props.member.name || 'This member' }}</strong>'s pool ticket code goes back to the pool, where another member can redeem it.
          </p>
          <p class="kb-dialog-warning">
            They'll be marked as unpaid.
          </p>
        </div>
        <template #footer>
          <Button
            label="Cancel"
            text
            severity="secondary"
            :disabled="isUpdating"
            @click="showUnredeemConfirm = false"
          />
          <Button
            label="Return to pool"
            severity="danger"
            :disabled="isUpdating"
            :loading="isUpdating"
            @click="unredeemPoolTicket"
          />
        </template>
      </Dialog>
    </div>

    <!-- Redeem code dialog -->
    <Dialog
      v-model:visible="showRedeemDialog"
      header="Redeem a code"
      modal
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          Enter a pool ticket code to apply to <strong>{{ props.member.name || 'this member' }}</strong>'s account.
        </p>
        <div class="kb-field">
          <label for="redeemCode">Redemption code</label>
          <InputText
            id="redeemCode"
            v-model="redeemCodeInput"
            size="small"
            fluid
            autofocus
            autocomplete="off"
            placeholder="e.g. GTJQQF2RM"
            class="font-mono uppercase"
            :invalid="!!redeemError"
            aria-describedby="redeemCode-error"
            :disabled="isRedeeming"
            @keyup.enter="redeemCodeForMember"
          />
          <small
            v-if="redeemError"
            id="redeemCode-error"
            class="kb-field-error"
          >{{ redeemError }}</small>
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          :disabled="isRedeeming"
          @click="closeRedeemDialog"
        />
        <Button
          label="Redeem"
          :disabled="!redeemCodeInput.trim() || isRedeeming"
          :loading="isRedeeming"
          @click="redeemCodeForMember"
        />
      </template>
    </Dialog>

    <!-- VIP upgrade -->
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          VIP upgrade
        </h4>
        <p
          v-if="vipPurchasedAt"
          class="kb-row-meta m-0 mt-0.5"
        >
          Since {{ formatDate(vipPurchasedAt) }}<span v-if="vipIssuerName"> · assigned by {{ vipIssuerName }}</span>
        </p>
        <p
          v-else
          class="kb-row-hint"
        >
          {{ hasVipTicket ? 'Has VIP for the upcoming parade' : 'No VIP for the upcoming parade' }}
        </p>
      </div>
      <div class="kb-row-aside">
        <span
          v-if="hasVipTicket"
          class="kb-tag kb-tag-honor"
        >VIP</span>
        <KbSpinner
          v-if="isUpdating"
          label="Saving"
        />
        <span
          v-if="boughtVip"
          class="kb-row-meta"
        >Bought · refund in Stripe to remove</span>
        <Button
          v-else
          :label="hasVipTicket ? 'Remove VIP' : 'Grant VIP'"
          :icon="hasVipTicket ? 'pi pi-times' : 'pi pi-star'"
          :severity="hasVipTicket ? 'danger' : 'secondary'"
          :text="hasVipTicket"
          :outlined="!hasVipTicket"
          :disabled="isUpdating"
          size="small"
          @click="toggleVipStatus"
        />
      </div>
    </div>

    <!-- Parking (captains, and members whose SubKrewe has a contraption) -->
    <div
      v-if="isCaptain || (isMember && subkreweHasContraption)"
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Parking {{ parkingPassCount > 1 ? 'passes' : 'pass' }}
        </h4>
        <p class="kb-row-hint">
          <template v-if="parkingPassCount === 0">
            No parking pass for the upcoming parade
          </template>
          <template v-else-if="parkingPassCount === 1">
            1 parking pass for the upcoming parade
          </template>
          <template v-else>
            {{ parkingPassCount }} parking passes for the upcoming parade
          </template>
        </p>
      </div>
      <div class="kb-row-aside">
        <span
          v-if="parkingPassCount > 0"
          class="kb-tag kb-tag-ok whitespace-nowrap"
        >{{ parkingPassCount }} pass{{ parkingPassCount > 1 ? 'es' : '' }}</span>
        <KbSpinner
          v-if="isUpdating"
          label="Saving"
        />
        <Button
          label="Add pass"
          icon="pi pi-plus"
          outlined
          severity="secondary"
          :disabled="isUpdating"
          size="small"
          @click="addParkingPass"
        />
      </div>
      <div
        v-if="parkingPasses.length > 0"
        class="kb-row-body"
      >
        <!-- Individual parking passes -->
        <ul
          v-if="parkingPasses.length > 0"
          class="m-0 p-0 list-none"
        >
          <li
            v-for="(pass, index) in parkingPasses"
            :key="pass.orderId"
            class="flex items-center justify-between gap-2 py-1 border-t border-kb-line first:border-t-0 text-sm"
          >
            <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
              <i class="pi pi-car !text-xs text-kb-dim" />
              <span>Pass {{ index + 1 }}</span>
              <span class="kb-row-meta">{{ formatDate(pass.purchasedAt) }}</span>
              <span
                v-if="isAdminGrant(pass)"
                class="kb-row-meta"
              >· admin assigned</span>
              <a
                v-else-if="pass.stripeSessionId || pass.stripePaymentIntentId"
                :href="pass.stripePaymentIntentId ? `https://dashboard.stripe.com/payments/${pass.stripePaymentIntentId}` : `https://dashboard.stripe.com/checkout/sessions/${pass.stripeSessionId}`"
                target="_blank"
                class="kb-row-meta hover:text-kb-accent hover:underline"
                :title="pass.stripePaymentIntentId || pass.stripeSessionId"
                @click.stop
              >· Stripe <i class="pi pi-arrow-up-right !text-[9px]" /></a>
              <a
                v-else-if="pass.orderId && /^\d+$/.test(pass.orderId)"
                :href="`https://www.eventbrite.com/organizations/orders/${pass.orderId}`"
                target="_blank"
                class="kb-row-meta hover:text-kb-accent hover:underline"
                :title="pass.orderId"
                @click.stop
              >· order {{ pass.orderId.length > 10 ? pass.orderId.slice(0, 10) + '…' : pass.orderId }} <i class="pi pi-arrow-up-right !text-[9px]" /></a>
            </div>
            <Button
              v-if="isAdminGrant(pass)"
              icon="pi pi-times"
              severity="danger"
              text
              rounded
              size="small"
              aria-label="Remove parking pass"
              :disabled="isUpdating"
              @click="removeParkingPass(pass.orderId)"
            />
          </li>
        </ul>
        <div
          v-if="grantedParkingCount > 1"
          class="kb-row-actions"
        >
          <Button
            label="Remove all granted"
            icon="pi pi-trash"
            severity="danger"
            text
            :disabled="isUpdating"
            size="small"
            @click="removeAllParkingPasses"
          />
        </div>
      </div>
    </div>

    <!-- Related Order IDs if available -->
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { DuesStatusType, getMemberDuesStatus } from '~/composables/useShouldPayDues.js'
import { purchaseDate, purchaseMillis } from '~/shared/dues/status.js'
import { isAdminGrant } from '~/shared/utils/adminGrants.js'
import type { ChildPurchaseItem, Member, PurchaseItem } from '~/shared/types/member.js'
import { formatDate } from '~/utils/dateUtils'
import { duesTagClass } from '~/utils/duesTag'

// Define proper interfaces for the purchases update structure
/**
 * A year's purchase buckets, as written back to the member document.
 *
 * The four arrays used to be re-declared inline here with the Eventbrite-era
 * fields required, which no longer matched what the Stripe webhook writes —
 * so an update built from real purchase items would not typecheck. They use
 * the shared types now.
 */
interface YearlyPurchaseData {
  hasMembership?: boolean
  membershipPurchasedAt?: Date
  hasVip?: boolean
  vipPurchasedAt?: Date
  orderIds?: string[]
  membership?: PurchaseItem[]
  vip?: PurchaseItem[]
  parking?: PurchaseItem[]
  children?: ChildPurchaseItem[]
}

interface PurchasesUpdate {
  [year: string]: YearlyPurchaseData
}

interface Props {
  member: Member
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'updated'): void
}>()
const toast = useToast()
const membersStore = useMembersStore()
const isUpdating = ref(false)

// Redemption code state
const isRedeeming = ref(false)
const showRedeemDialog = ref(false)
const redeemCodeInput = ref('')
const redeemError = ref<string | null>(null)
const showUnredeemConfirm = ref(false)

// Get the current parade year from runtime config
const { paradeYear } = useParadeYear()

// Use the centralized dues status logic
const duesStatus = computed(() => getMemberDuesStatus(props.member))

// Check for pooled membership purchase info
const pooledMembershipInfo = computed(() => {
  const yearlyPurchases = props.member.purchases?.[paradeYear.value]
  if (!yearlyPurchases?.membership || !Array.isArray(yearlyPurchases.membership)) return null

  // `pooled` and its companions are written only for pool redemptions, so
  // they are not on PurchaseItem — read them off a widened view.
  const pooledPurchase = (yearlyPurchases.membership as Array<PurchaseItem & {
    pooled?: boolean
    redeemCode?: string
    subkreweId?: string
    poolTicketId?: string
    redeemedByAdmin?: string
  }>).find(p => p.pooled === true)

  if (!pooledPurchase) return null

  // Try to get subkrewe name from member's current subkrewe or look it up
  const subkreweName = props.member.subkrewe?.name || null

  // Get admin name if redeemed by admin
  const redeemedByAdminName = pooledPurchase.redeemedByAdmin
    ? getAdminName(pooledPurchase.redeemedByAdmin)
    : null

  return {
    redeemCode: pooledPurchase.redeemCode || null,
    subkreweId: pooledPurchase.subkreweId,
    poolTicketId: pooledPurchase.poolTicketId,
    subkreweName,
    redeemedByAdminName,
  }
})

// Check for admin-comped membership (not pooled, but has issuedBy)
const adminCompedMembershipInfo = computed(() => {
  const yearlyPurchases = props.member.purchases?.[paradeYear.value]
  if (!yearlyPurchases?.membership || !Array.isArray(yearlyPurchases.membership)) return null

  const adminPurchase = yearlyPurchases.membership.find(
    (p: { pooled?: boolean, issuedBy?: string }) => !p.pooled && p.issuedBy,
  ) as { issuedBy?: string } | undefined

  if (!adminPurchase?.issuedBy) return null

  return {
    adminName: getAdminName(adminPurchase.issuedBy),
  }
})

// Get admin name by uid
function getAdminName(uid?: string): string {
  if (!uid) return 'Unknown'
  const member = membersStore.members[`members/${uid}`]
  return member?.name || uid.substring(0, 8) + '...'
}

// Check if member is a captain
const isCaptain = computed(() => {
  return props.member.member_type === 'captain'
})

// Check if member is a regular member (not redshirt, pending, or captain)
const isMember = computed(() => {
  return props.member.member_type === 'member'
})

// Check if member's SubKrewe has a contraption
const subkreweHasContraption = computed(() => {
  return props.member.subkrewe?.contraption === true
})

const hasVipTicket = computed(() => {
  const yearlyPurchases = props.member.purchases?.[paradeYear.value]
  if (yearlyPurchases?.vip && Array.isArray(yearlyPurchases.vip) && yearlyPurchases.vip.length > 0) {
    return yearlyPurchases.vip.some(purchase => purchase.status === 'completed')
  }
  return false
})

// Payments, as opposed to admin grants, can't be taken back here: only a
// refund in Stripe (which marks the record refunded) undoes one
const paidMembership = computed(() => (props.member.purchases?.[paradeYear.value]?.membership ?? [])
  .some(p => p.status === 'completed' && !isAdminGrant(p) && !(p as { pooled?: boolean }).pooled))
const boughtVip = computed(() => (props.member.purchases?.[paradeYear.value]?.vip ?? [])
  .some(p => p.status === 'completed' && !isAdminGrant(p)))

const vipPurchasedAt = computed(() => {
  const yearlyPurchases = props.member.purchases?.[paradeYear.value]

  if (yearlyPurchases?.vip && Array.isArray(yearlyPurchases.vip) && yearlyPurchases.vip.length > 0) {
    // Find the most recent completed purchase
    const completedPurchases = yearlyPurchases.vip.filter(p => p.status === 'completed')
    if (completedPurchases.length > 0) {
      const latest = completedPurchases.sort((a, b) => purchaseMillis(b) - purchaseMillis(a))[0]
      return latest ? purchaseDate(latest) : null
    }
  }

  return null
})

// Get VIP issuer information
const vipIssuedBy = computed(() => {
  const yearlyPurchases = props.member.purchases?.[paradeYear.value]

  if (yearlyPurchases?.vip && Array.isArray(yearlyPurchases.vip) && yearlyPurchases.vip.length > 0) {
    const completedPurchases = yearlyPurchases.vip.filter(p => p.status === 'completed')
    if (completedPurchases.length > 0) {
      const mostRecent = completedPurchases.sort((a, b) => purchaseMillis(b) - purchaseMillis(a))[0]

      // Check if it's an admin-assigned VIP
      if (mostRecent?.orderId?.startsWith('admin-') || mostRecent?.ticketType?.toLowerCase().includes('admin')) {
        return mostRecent.issuedBy || null
      }
    }
  }

  return null
})

// Get issuer name for VIP
const vipIssuerName = ref<string | null>(null)
watch(vipIssuedBy, async (uid) => {
  if (!uid) {
    vipIssuerName.value = null
    return
  }

  try {
    let issuer = membersStore.members[`members/${uid}`]
    if (!issuer) {
      await membersStore.fetchMember(uid)
      issuer = membersStore.members[`members/${uid}`]
    }
    vipIssuerName.value = issuer?.name || 'Unknown'
  }
  catch {
    vipIssuerName.value = 'Unknown'
  }
}, { immediate: true })

// Check if member has a parking pass
// Get all completed parking passes
const parkingPasses = computed(() => {
  const yearlyPurchases = props.member.purchases?.[paradeYear.value]
  if (yearlyPurchases?.parking && Array.isArray(yearlyPurchases.parking)) {
    return yearlyPurchases.parking
      .filter(p => p.status === 'completed')
      .sort((a, b) => purchaseMillis(a) - purchaseMillis(b))
  }
  return []
})

// Count of parking passes
const parkingPassCount = computed(() => parkingPasses.value.length)
const grantedParkingCount = computed(() => parkingPasses.value.filter(p => isAdminGrant(p)).length)

// Every record of a kind for the parade year. A write replaces the year's
// whole array, so it starts from these, refunded records included, and
// changes only the record it is about.
const yearRecords = async (kind: 'membership' | 'vip' | 'parking'): Promise<PurchaseItem[]> => {
  // Read fresh: the drawer's copy can be older than a payment the Stripe
  // webhook recorded since, and writing it back would drop that payment
  const fresh = await membersStore.fetchMember(props.member.id)
  const records = (fresh ?? props.member).purchases?.[paradeYear.value]?.[kind]
  return Array.isArray(records) ? [...records] : []
}

// Helper functions for ticket type display

// Toggle membership payment status
const toggleMembershipPayment = async () => {
  if (isUpdating.value) return
  isUpdating.value = true

  try {
    const currentUser = useCurrentUser()
    const newStatus = duesStatus.value.statusType !== DuesStatusType.PAID
    const now = new Date()
    const adminOrderId = `admin-${Date.now()}` // Generate an admin order ID

    // Create purchase object in the new format
    const purchaseItem = {
      orderId: adminOrderId,
      purchasedAt: now,
      ticketType: 'membership',
      accessCode: 'admin-override',
      status: 'completed',
      eventbriteOrderId: adminOrderId,
      issuedBy: currentUser.value?.uid || 'unknown',
    }

    // Create a properly nested object structure for the purchases update
    const purchasesUpdate: PurchasesUpdate = {}

    // Marking paid adds a grant next to whatever is there (a refunded
    // payment, say); marking unpaid takes back only the grants. A payment
    // stays: the button isn't offered for one, and a refund in Stripe is
    // what undoes it.
    const records = await yearRecords('membership')
    if (!newStatus && records.some(p => p.status === 'completed' && !isAdminGrant(p) && !(p as { pooled?: boolean }).pooled)) {
      throw new Error('This membership was paid for; refund it in Stripe instead')
    }
    purchasesUpdate[paradeYear.value] = {
      membership: newStatus
        ? [...records, purchaseItem]
        : records.filter(p => !(p.status === 'completed' && isAdminGrant(p))),
    }

    // `pending` means the signup flow was never completed. Nothing about paying
    // dues promotes a member off it — not this comp path and not the Stripe
    // webhook — so a comped pending member ends up paid but broken: Discourse
    // sync skips pending (runDiscourseSync), check-in search filters them out
    // (server/api/members/search.get.ts, checkin scope), and Mailchimp tags them
    // role:pending. Marking dues paid is an explicit admin statement that this
    // person is a paid member, so promote them. Marking unpaid never demotes —
    // that would silently strip forum and check-in access.
    const promoteFromPending = newStatus && props.member.member_type === 'pending'

    // Update the member
    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
      ...(promoteFromPending ? { member_type: 'member' as const } : {}),
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: newStatus ? 'success' : 'info',
      summary: newStatus ? 'Membership Paid' : 'Membership Unpaid',
      detail: newStatus
        ? `${props.member.name} has been marked as paid for ${paradeYear.value} membership dues${promoteFromPending ? ' and promoted from pending to member' : ''}`
        : `${props.member.name} has been marked as unpaid for ${paradeYear.value} membership dues`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error updating membership payment status:', error)
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Could not update membership payment status',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

// Toggle VIP ticket status
const toggleVipStatus = async () => {
  if (isUpdating.value) return
  isUpdating.value = true

  try {
    const currentUser = useCurrentUser()
    const newStatus = !hasVipTicket.value
    const now = new Date()
    const adminOrderId = `admin-${Date.now()}` // Generate an admin order ID

    // Create purchase object in the new format
    const purchaseItem = {
      orderId: adminOrderId,
      purchasedAt: now,
      ticketType: 'Admin Assigned VIP',
      accessCode: 'admin-override',
      status: 'completed',
      eventbriteOrderId: adminOrderId,
      issuedBy: currentUser.value?.uid || 'unknown',
    }

    // Granting adds a record; removing takes back the granted ones. A bought
    // VIP isn't offered for removal (a refund in Stripe undoes it), and
    // refunded records stay.
    const vipRecords = await yearRecords('vip')
    if (!newStatus && vipRecords.some(p => p.status === 'completed' && !isAdminGrant(p))) {
      throw new Error('This VIP was bought; refund it in Stripe instead')
    }
    const purchasesUpdate: PurchasesUpdate = {
      [paradeYear.value]: {
        vip: newStatus
          ? [...vipRecords, purchaseItem]
          : vipRecords.filter(p => !(p.status === 'completed' && isAdminGrant(p))),
      },
    }

    // Update the member
    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: newStatus ? 'success' : 'info',
      summary: newStatus ? 'VIP Status Granted' : 'VIP Status Removed',
      detail: newStatus
        ? `${props.member.name} has been granted VIP status for ${paradeYear.value}`
        : `${props.member.name} no longer has VIP status for ${paradeYear.value}`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error updating VIP status:', error)
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Could not update VIP ticket status',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}
// Add a single parking pass
const addParkingPass = async () => {
  if (isUpdating.value) return
  isUpdating.value = true

  try {
    const currentUser = useCurrentUser()
    const now = new Date()
    const adminOrderId = `admin-${Date.now()}`

    const purchaseItem = {
      orderId: adminOrderId,
      purchasedAt: now,
      ticketType: 'parking',
      accessCode: 'admin-override',
      status: 'completed',
      eventbriteOrderId: adminOrderId,
      issuedBy: currentUser.value?.uid || 'unknown',
    }

    const passCount = parkingPasses.value.length + 1
    const purchasesUpdate: PurchasesUpdate = {
      [paradeYear.value]: {
        parking: [...await yearRecords('parking'), purchaseItem],
      },
    }

    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: 'success',
      summary: 'Parking Pass Added',
      detail: `${props.member.name} now has ${passCount} parking pass${passCount > 1 ? 'es' : ''} for ${paradeYear.value}`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error adding parking pass:', error)
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Could not add parking pass',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

// Remove a specific parking pass by orderId
const removeParkingPass = async (orderId: string) => {
  if (isUpdating.value) return
  isUpdating.value = true

  try {
    const remainingPasses = parkingPasses.value.filter(p => p.orderId !== orderId)

    const purchasesUpdate: PurchasesUpdate = {
      [paradeYear.value]: {
        parking: (await yearRecords('parking')).filter(p => !(p.orderId === orderId && p.status === 'completed' && isAdminGrant(p))),
      },
    }

    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: 'info',
      summary: 'Parking Pass Removed',
      detail: remainingPasses.length > 0
        ? `${props.member.name} now has ${remainingPasses.length} parking pass${remainingPasses.length > 1 ? 'es' : ''} for ${paradeYear.value}`
        : `${props.member.name} no longer has any parking passes for ${paradeYear.value}`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error removing parking pass:', error)
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Could not remove parking pass',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

// Remove all parking passes
const removeAllParkingPasses = async () => {
  if (isUpdating.value) return
  isUpdating.value = true

  try {
    const purchasesUpdate: PurchasesUpdate = {
      [paradeYear.value]: {
        parking: (await yearRecords('parking')).filter(p => !(p.status === 'completed' && isAdminGrant(p))),
      },
    }

    const saved = await membersStore.updateMember(props.member, {
      purchases: purchasesUpdate,
    } as Partial<Member>)
    if (!saved) {
      throw new Error('The member update was not saved')
    }

    toast.add({
      severity: 'info',
      summary: 'Granted Parking Passes Removed',
      detail: `Removed the parking passes granted to ${props.member.name} for ${paradeYear.value}; bought passes stay`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('Error removing parking passes:', error)
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'Could not remove parking passes',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

// Redeem code on behalf of member
const closeRedeemDialog = () => {
  showRedeemDialog.value = false
  redeemCodeInput.value = ''
  redeemError.value = null
}

const redeemCodeForMember = async () => {
  if (isRedeeming.value || !redeemCodeInput.value.trim()) return
  isRedeeming.value = true
  redeemError.value = null

  try {
    const res = await $fetch('/api/admin/pool/redeem', {
      method: 'POST',
      body: {
        code: redeemCodeInput.value.trim(),
        memberId: props.member.id,
      },
    }) as { success: boolean, message?: string, subkreweId?: string }

    if (!res.success) {
      redeemError.value = res.message || 'Failed to redeem code'
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Code Redeemed',
      detail: `Successfully redeemed code for ${props.member.name}`,
      life: 3000,
    })

    closeRedeemDialog()
    emit('updated')
  }
  catch (error) {
    console.error('Error redeeming code:', error)
    redeemError.value = error instanceof Error ? error.message : 'Failed to redeem code'
  }
  finally {
    isRedeeming.value = false
  }
}

// Unredeem pool ticket and return it to the pool
const unredeemPoolTicket = async () => {
  if (isUpdating.value || !pooledMembershipInfo.value) return
  isUpdating.value = true

  try {
    const { subkreweId, poolTicketId } = pooledMembershipInfo.value
    if (!subkreweId || !poolTicketId) {
      throw new Error('Missing ticket information')
    }

    const res = await $fetch('/api/admin/pool/unredeem', {
      method: 'POST',
      body: {
        memberId: props.member.id,
        poolTicketId,
        subkreweId,
      },
    }) as { success: boolean, message?: string }

    if (!res.success) {
      throw new Error(res.message || 'Failed to return ticket to pool')
    }

    toast.add({
      severity: 'success',
      summary: 'Ticket Returned',
      detail: `Pool ticket returned. ${props.member.name} is now marked as unpaid.`,
      life: 3000,
    })

    showUnredeemConfirm.value = false
    emit('updated')
  }
  catch (error) {
    console.error('Error unredeeing pool ticket:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error instanceof Error ? error.message : 'Failed to return ticket to pool',
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}
</script>

<style scoped>
/* No special styles needed */
</style>
